// src/pages/ReservationPageA.js
import React, { useState, useEffect, useMemo } from "react";
import { initializeApp } from "firebase/app";
import { getDatabase, ref, set, onValue, remove, off } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import {
  fetchAdminLevels,
  getAdminLevel,
  getUserType,
} from "../utils/userAccess";
import {
  canUploadFiles,
  reservationRules,
} from "../utils/accessRules";
import { UserRoundCheck, Calendar1 } from "lucide-react";
import DatePicker from "react-datepicker";
import { EMAILJS } from "../config/emailConfig";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/datepicker-dark.css";
import AppTopBar from "../components/AppTopBar";
import AppFooter from "../components/AppFooter";

// ---- Local date helpers (file-scope, reusable) ----
function formatYMDLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYMDLocal(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d); // local midnight
}

function combineLocalDateAndTime(dateObj, hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return new Date(
    dateObj.getFullYear(),
    dateObj.getMonth(),
    dateObj.getDate(),
    h,
    m || 0,
    0,
    0
  );
}

function normalizeTimeStatic(time) {
  const [hours, minutes] = time.split(":").map(Number);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

// 0=Sun..6=Sat (local)
function isRecurringMatch(dayIndex, recurring) {
  return (
    !recurring ||
    (recurring === "Sun" && dayIndex === 0) ||
    (recurring === "Mon" && dayIndex === 1) ||
    (recurring === "Tue" && dayIndex === 2) ||
    (recurring === "Wed" && dayIndex === 3) ||
    (recurring === "Thu" && dayIndex === 4) ||
    (recurring === "Fri" && dayIndex === 5) ||
    (recurring === "Sat" && dayIndex === 6) ||
    (recurring === "Mon-Thu" && dayIndex >= 1 && dayIndex <= 4) ||
    (recurring === "Mon-Fri" && dayIndex >= 1 && dayIndex <= 5) ||
    (recurring === "Sat-Sun" && (dayIndex === 6 || dayIndex === 0))
  );
}

const scheduleTimesStatic = Array.from({ length: 32 }, (_, i) => {
  const totalMinutes = 8 * 60 + i * 30;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
});

function makeSlotKey(room, dateKey, time) {
  return `${room}__${dateKey}__${time}`;
}

function buildScheduleLookup(items) {
  const map = {};

  items.forEach((item) => {
    const room = item.Classroom?.trim();
    const weeks = item.Week || [];
    const start = normalizeTimeStatic(item.StartTime);
    const end = normalizeTimeStatic(item.EndTime);

    weeks.forEach((dateKey) => {
      scheduleTimesStatic.forEach((time) => {
        if (time >= start && time < end) {
          map[makeSlotKey(room, dateKey, time)] = item;
        }
      });
    });
  });

  return map;
}

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAaR9ZU1w_bFxMKMbNxu-9eBpuOOGm1Eow",
  authDomain: "bookingappv1-d1e2d.firebaseapp.com",
  databaseURL: "https://bookingappv1-d1e2d-default-rtdb.firebaseio.com/",
  projectId: "bookingappv1-d1e2d",
  storageBucket: "bookingappv1-d1e2d.appspot.com",
  messagingSenderId: "134532713601",
  appId: "1:134532713601:web:5ad89fec3f4bf09b519180",
};

const app = initializeApp(firebaseConfig);
const db = getDatabase(app);
const auth = getAuth(app);

const SPECIAL_APPROVAL_ROOMS = [
  "CMA_Auditório",
  "CMA_Sala_de_Espelhos",
  "CMA_Biblioteca",
];

// --- helpers for domains / levels / special rooms ---
const isUEStudent = (email = "") =>
  email.toLowerCase().endsWith("@alunos.uevora.pt");

const isUEStaff = (email = "") =>
  email.toLowerCase().endsWith("@uevora.pt");

const inAdminLevels = (lvl) => [0, 1, 2, 3, 4].includes(Number(lvl));

const isSpecialRoom = (room) => SPECIAL_APPROVAL_ROOMS.includes(room);

// --- student period rules (30-minute aware) ---
const STUDENT_PERIODS = [
  { key: "P1", label: "9–14", startMinutes: 9 * 60, endMinutes: 14 * 60, maxHours: 2 },
  { key: "P2", label: "14–19", startMinutes: 14 * 60, endMinutes: 19 * 60, maxHours: 2 },
  { key: "P3", label: "19–23", startMinutes: 19 * 60, endMinutes: 23 * 60, maxHours: 2 },
];

const timeToMinutes = (timeStr) => {
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + (m || 0);
};

const getPeriodKey = (timeStr) => {
  const mins = timeToMinutes(timeStr);
  const p = STUDENT_PERIODS.find(
    (pr) => mins >= pr.startMinutes && mins < pr.endMinutes
  );
  return p ? p.key : null;
};

const getPeriodByKey = (key) => STUDENT_PERIODS.find((p) => p.key === key);

const isWeekendDate = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday, 6 = Saturday
  return day === 0 || day === 6;
};

const getPreviousFridayCutoff = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 6=Sat

  const daysBack = day === 6 ? 1 : 2;

  const friday = new Date(d);
  friday.setDate(friday.getDate() - daysBack);
  friday.setHours(13, 0, 0, 0);

  return friday;
};

const addMinutesToTime = (timeStr, minutesToAdd) => {
  const [h, m] = timeStr.split(":").map(Number);
  const total = h * 60 + m + minutesToAdd;
  const newH = Math.floor(total / 60);
  const newM = total % 60;
  return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
};

export default function ReservationPageA() {
  const [reservations, setReservations] = useState({});
  const [restrictedSchedules, setRestrictedSchedules] = useState([]);
  const [unavailableSchedules, setUnavailableSchedules] = useState([]);
  const [roomAccess, setRoomAccess] = useState({});
  const [rooms, setRooms] = useState([]);
  const [user, setUser] = useState(null);
  const [currentDay, setCurrentDay] = useState(new Date().getDay());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const approverEmail = EMAILJS?.approvals?.approverEmail?.toLowerCase?.() || "";
  const isApprover =
  (user?.email?.toLowerCase?.() === approverEmail) ||
  userData?.adminLevel === 0 ||
  userData?.adminLevel === 1;

  // map of pending slots
  const [pendingSlots, setPendingSlots] = useState({});

  useEffect(() => {
    const approvalsRef = ref(db, "approvalRequests");
    const cb = (snap) => {
      const val = snap.val() || {};
      const map = {};
      Object.values(val).forEach((req) => {
        if (req?.status === "pending" && req?.room && req?.period?.slots) {
          const room = req.room;
          req.period.slots.forEach(({ dateKey, time }) => {
            if (!map[room]) map[room] = {};
            if (!map[room][dateKey]) map[room][dateKey] = {};
            map[room][dateKey][time] = {
              requesterName: req.requestedBy?.name || "",
              requesterEmail: req.requestedBy?.email || "",
            };
          });
        }
      });
      setPendingSlots(map);
    };
    onValue(approvalsRef, cb);
    return () => off(approvalsRef, "value", cb);
  }, []);

  useEffect(() => {
    if (!isApprover) return;
    const approvalsRef = ref(db, "approvalRequests");
    const cb = (snap) => {
      const val = snap.val() || {};
      const count = Object.values(val).filter((r) => r?.status === "pending").length;
      setPendingCount(count);
    };
    onValue(approvalsRef, cb);
    return () => off(approvalsRef, "value", cb);
  }, [isApprover]);

  const normalizeTime = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  const normalizeWeekDates = (weeks, weekdayName) => {
    const daysMapping = {
      Domingo: 0,
      Segunda: 1,
      Terça: 2,
      Quarta: 3,
      Quinta: 4,
      Sexta: 5,
      Sábado: 6,
    };

    if (!Object.prototype.hasOwnProperty.call(daysMapping, weekdayName)) return [];

    return weeks.map((weekStart) => {
      const startDate = parseYMDLocal(weekStart);
      const correctedDate = new Date(startDate);
      correctedDate.setDate(startDate.getDate() + (daysMapping[weekdayName] - 1));
      return formatYMDLocal(correctedDate);
    });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const email = user.email;

        const adminMap = await fetchAdminLevels();
        const level = getAdminLevel(email, adminMap);
        const type = getUserType(email);

        setUserData({
          email,
          adminLevel: level,
          userType: type,
          canUpload: canUploadFiles(level),
          reservationRules: reservationRules(level),
        });

        setUser(user);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const reservationsRef = ref(db, "reservations/buildingA");
    onValue(reservationsRef, (snapshot) => {
      setReservations(snapshot.val() || {});
    });
  }, []);

  useEffect(() => {
  const schedulesRef = ref(db, "uploads/class_schedulesA");

  onValue(schedulesRef, (snapshot) => {
    const content = snapshot.val()?.content;
    if (!content) return;

    Papa.parse(content, {
      header: true,
      skipEmptyLines: true,

      complete: (result) => {
        const dayMapping = {
          0: "Segunda",
          1: "Terça",
          2: "Quarta",
          3: "Quinta",
          4: "Sexta",
          5: "Sábado",
          6: "Domingo",
        };

        const parsedData = result.data
          .filter((row) => row.ClassroomName?.trim())
          .map((row) => {
            const eventDay = Number(row.EventDay);

            const weekdayName = dayMapping[eventDay] ?? "";

            return {
              ...row,

              // New CSV → existing internal field names
              Classroom: row.ClassroomName?.trim() || "",

              StartTime: row.EventStartTime?.trim() || "",

              EndTime: row.EventEndTime?.trim() || "",

              Week: row.WeekStartDate
                ? normalizeWeekDates(
                    row.WeekStartDate.split(","),
                    weekdayName
                  )
                : [],

              WeekdayName: weekdayName,

              ModuleName: row.ModuleName?.trim() || "",
              ModuleAcronym: row.ModuleAcronym?.trim() || "",
              ModuleCode: row.ModuleCode?.trim() || "",
            };
          });

        setRestrictedSchedules(parsedData);
      },
    });
  });
}, []);

  useEffect(() => {
    const unavailableRef = ref(db, "uploads/unavailable_schedules");
    onValue(unavailableRef, (snapshot) => {
      const content = snapshot.val()?.content;
      if (!content) return;
      Papa.parse(content, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const processed = [];
          result.data.forEach((row) => {
            const classroom = row.Classroom?.trim();
            const start = row.StartTime ? normalizeTime(row.StartTime) : "00:00";
            const end = row.EndTime ? normalizeTime(row.EndTime) : "24:00";
            const fromDate = row.FromDate?.trim();
            const toDate = row.ToDate?.trim();
            const recurring = row.Recurring?.trim();

            for (
              let d = parseYMDLocal(fromDate);
              d <= parseYMDLocal(toDate);
              d.setDate(d.getDate() + 1)
            ) {
              const dayIndex = d.getDay();
              const dateStr = formatYMDLocal(d);
              if (isRecurringMatch(dayIndex, recurring)) {
                processed.push({
                  Classroom: classroom,
                  StartTime: start,
                  EndTime: end,
                  Week: [dateStr],
                });
              }
            }
          });
          setUnavailableSchedules(processed);
        },
      });
    });
  }, []);

  useEffect(() => {
    const accessRef = ref(db, "uploads/room_access");
    onValue(accessRef, (snapshot) => {
      const content = snapshot.val()?.content;
      if (!content) return;
      Papa.parse(content, {
        header: true,
        delimiter: ";",
        skipEmptyLines: true,
        complete: (result) => {
          const accessData = {};
          result.data.forEach((row) => {
            const classroom = row.Classroom?.trim();
            if (!classroom) return;
            const accessRules = [];
            if (row.Allowed1) {
              accessRules.push({
                type: row.Allowed1.trim().toLowerCase(),
                from: row.FromDate1?.trim() || null,
                to: row.ToDate1?.trim() || null,
              });
            }
            if (row.Allowed2) {
              accessRules.push({
                type: row.Allowed2.trim().toLowerCase(),
                from: row.FromDate2?.trim() || null,
                to: row.ToDate2?.trim() || null,
              });
            }
            accessData[classroom] = accessRules;
          });
          setRoomAccess(accessData);
        },
      });
    });
  }, []);

  useEffect(() => {
    const roomsRef = ref(db, "uploads/rooms");
    onValue(roomsRef, (snapshot) => {
      const content = snapshot.val()?.content;
      if (!content) return;
      try {
        const parsed = JSON.parse(content);
        if (parsed.buildingA) setRooms(parsed.buildingA);
      } catch (err) {
        console.error("Error parsing rooms.json:", err);
      }
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".menu-dropdown")) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isRoomAccessible = (room, userType, date = new Date()) => {
    const accessRules = roomAccess[room] || [];
    const dateStr = formatYMDLocal(date);

    return accessRules.some((rule) => {
      if (rule.type !== userType) return false;
      if (!rule.from && !rule.to) return true;
      if (rule.from && dateStr < rule.from) return false;
      if (rule.to && dateStr > rule.to) return false;
      return true;
    });
  };

  const handleReserve = (room, time) => {
    if (!user) {
      alert("Please log in to make a reservation.");
      return;
    }

    const email = user.email || "";
    const emailLC = email.toLowerCase();
    const dateKey = formatYMDLocal(currentDate);
    const timeKey = time;

    const level = userData?.adminLevel;
    const isAdminFull = level === 0;
    const isAdminGeneral = level === 1 || level === 2;
    const isStudentLevel = level === 3 || level === 4;
    const studentDomain = isUEStudent(emailLC);
    const staffDomain = isUEStaff(emailLC);
    const isAdminUser = isAdminFull || isAdminGeneral;
    const userType = studentDomain ? "students" : "faculty";

    if (studentDomain && !isStudentLevel) {
      alert("Only authorized students can make reservations.");
      return;
    }

    const isStaffOther = staffDomain && !inAdminLevels(level);
    const wantsSpecialRoom = isSpecialRoom(room);

    const goToApprovalIfSlotFree = () => {
      const isTaken = !!reservations[room]?.[dateKey]?.[timeKey];

      const restrictedSchedule =
        restrictedLookup[makeSlotKey(room, dateKey, timeKey)] || null;

      const unavailableSchedule =
        unavailableLookup[makeSlotKey(room, dateKey, timeKey)] ||
        unavailableLookup[makeSlotKey("ALL", dateKey, timeKey)] ||
        null;

      if (isTaken || restrictedSchedule || unavailableSchedule) {
        alert("That slot is not available for request.");
        return;
      }

      navigate("/request-approval", {
        state: {
          room,
          beginDate: dateKey,
          beginTime: timeKey,
          endDate: dateKey,
          endTime: addMinutesToTime(timeKey, 30),
          requesterName: user.displayName || "",
          requesterEmail: email,
        },
      });
    };

    {
      const now = new Date();

      if (isWeekendDate(currentDate)) {
        const cutoff = getPreviousFridayCutoff(currentDate);

        if (now >= cutoff) {
          alert("Weekend reservations must be made before 13:00 on the previous Friday.");
          return;
        }
      }
    }

    if (isAdminGeneral && wantsSpecialRoom) {
      goToApprovalIfSlotFree();
      return;
    }

    if (isStaffOther && wantsSpecialRoom) {
      goToApprovalIfSlotFree();
      return;
    }

    const allowed = isAdminUser || isRoomAccessible(room, userType, currentDate);
    if (!allowed) {
      alert("You do not have permission to book this room.");
      return;
    }

    if (!isAdminFull) {
      const reservationDateTime = combineLocalDateAndTime(currentDate, timeKey);
      const now = new Date();
      if (reservationDateTime < now) {
        alert("You cannot reserve past time slots.");
        return;
      }
    }

    if (isStudentLevel) {
      const now = new Date();

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const target = new Date(currentDate);
      target.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const isToday = target.getTime() === today.getTime();
      const isTomorrow = target.getTime() === tomorrow.getTime();
      const isWeekend = isWeekendDate(currentDate);

      if (level === 3) {
        if (isToday) {
          // allowed today
        } else if (isWeekend) {
          // allowed before Friday 13:00 due to global rule above
        } else if (isTomorrow) {
          if (now.getHours() < 22) {
            alert("You can only reserve for tomorrow after 22:00 today.");
            return;
          }
        } else {
          alert("You can only reserve for today, for tomorrow after 22:00, or for the weekend before Friday 13:00.");
          return;
        }
      }

      if (level === 4) {
        if (isToday) {
          // allowed today
        } else if (isWeekend) {
          // allowed before Friday 13:00 due to global rule above
        } else if (isTomorrow) {
          if (now.getHours() < 20) {
            alert("You can only reserve for tomorrow after 20:00 today.");
            return;
          }
        } else {
          alert("You can only reserve for today, for tomorrow after 20:00, or for the weekend before Friday 13:00.");
          return;
        }
      }

      const alreadyReserved = Object.values(reservations).some(
        (roomReservations) => roomReservations?.[dateKey]?.[timeKey]?.email === email
      );
      if (alreadyReserved) {
        alert("You can only reserve one room at the same time.");
        return;
      }

      {
        const perPeriodCounts = { P1: 0, P2: 0, P3: 0 };

        for (const [, times] of Object.entries(reservations)) {
          const dayReservations = times?.[dateKey];
          if (!dayReservations) continue;

          for (const [t, res] of Object.entries(dayReservations)) {
            if (res?.email === email) {
              const pk = getPeriodKey(t);
              if (pk) perPeriodCounts[pk] += 0.5;
            }
          }
        }

        const selectedPk = getPeriodKey(timeKey);
        if (!selectedPk) {
          alert("Students may reserve up to 2 hours per period: 9–14h, 14–19h, or 19–23h.");
          return;
        }

        const { maxHours, label } = getPeriodByKey(selectedPk);
        if (perPeriodCounts[selectedPk] + 0.5 > maxHours) {
          alert(`You can only reserve ${maxHours} hours in the ${label} period.`);
          return;
        }
      }
    }

    set(ref(db, `reservations/buildingA/${room}/${dateKey}/${timeKey}`), {
      status: "Reserved",
      email: email,
      user: user.displayName,
    });
  };

  const handleCancel = (room, time) => {
    if (!user) {
      alert("Please log in to cancel a reservation.");
      return;
    }

    const email = user.email || "";
    const emailLC = email.toLowerCase();
    const dateKey = formatYMDLocal(currentDate);
    const reservation = reservations[room]?.[dateKey]?.[time];

    if (!reservation) {
      alert("No reservation found.");
      return;
    }

    const level = userData?.adminLevel;
    const isAdminFull = level === 0;
    const isAdminGeneral = level === 1 || level === 2;
    const isStudentLevel = level === 3 || level === 4;
    const isFacultyOther = emailLC.endsWith("@uevora.pt") && !inAdminLevels(level);
    const isOwnReservation = reservation.email === email;

    const reservationDateTime = combineLocalDateAndTime(currentDate, time);
    const now = new Date();
    const isPast = reservationDateTime < now;

    if (isPast && !isAdminFull) {
      alert("You do not have permission to cancel past reservations.");
      return;
    }

    if ((isAdminFull || isAdminGeneral) && !isOwnReservation) {
      set(ref(db, `reservations/buildingA/${room}/${dateKey}/${time}`), {
        status: "Removed",
        admin: email,
      })
        .then(() =>
          console.log(`Admin ${email} removed a reservation for ${reservation.email}`)
        )
        .catch((error) => console.error("Admin remove error: ", error));
      return;
    }

    if (isOwnReservation && (isAdminFull || isAdminGeneral || isStudentLevel || isFacultyOther)) {
      remove(ref(db, `reservations/buildingA/${room}/${dateKey}/${time}`))
        .then(() => console.log(`User ${email} canceled their reservation`))
        .catch((error) => console.error("Cancellation error: ", error));
      return;
    }

    alert("You can only cancel your own reservations.");
  };

  const scheduleTimes = scheduleTimesStatic;

  const changeDay = (direction) => {
    setCurrentDay((prevDay) => (prevDay + direction + 7) % 7);
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(prevDate.getDate() + direction);
      return newDate;
    });
  };

  const dateKey = formatYMDLocal(currentDate);

  const restrictedLookup = useMemo(() => {
    return buildScheduleLookup(restrictedSchedules);
  }, [restrictedSchedules]);

  const unavailableLookup = useMemo(() => {
    return buildScheduleLookup(unavailableSchedules);
  }, [unavailableSchedules]);

  const studentAccessibleRooms = useMemo(() => {
    const map = {};
    rooms.forEach((room) => {
      map[room] = isRoomAccessible(room, "students", currentDate);
    });
    return map;
  }, [rooms, roomAccess, currentDate]);

  const formattedDate = `${currentDate.getDate()} of ${currentDate.toLocaleString(
    "default",
    { month: "long" }
  )} of ${currentDate.getFullYear()}`;

  const goToContact = () => navigate("/contact");
  const goToTerms = () => navigate("/terms-of-use");

  return (
    <div className="flex flex-col p-6 pt-28 w-full bg-gray-800 text-gray-100 min-h-screen">
      <AppTopBar />

      <div className="w-full mb-8 px-6">
        <h1 className="text-xl font-bold mb-4 text-left">
          Reservations for Mateus d&apos;Aranda
        </h1>

        <div className="flex justify-end">
          <button
            onClick={() => setIsModalOpen(true)}
            className="text-white px-3 py-1.5 rounded bg-purple-800 hover:bg-purple-900"
          >
            <div className="flex gap-2">
              Select a Date
              <Calendar1 className="w-5 h-5 text-white" />
            </div>
          </button>
        </div>
      </div>

      <div className="flex justify-center items-center mb-4 relative">
        <div className="flex items-center gap-4">
          <button
            onClick={() => changeDay(-1)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded"
          >
            ⬅
          </button>

          <h2 className="text-lg font-semibold text-white">{formattedDate}</h2>

          <button
            onClick={() => changeDay(1)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded"
          >
            ➡
          </button>
        </div>
      </div>

      <div className="w-full overflow-auto max-h-[65vh] rounded-lg border border-gray-600 mb-8">
        <table className="border-separate border-spacing-0 min-w-max w-full">
          <thead>
            <tr className="text-white">
              <th className="sticky top-0 left-0 z-30 bg-gray-700 border border-gray-600 p-2 text-left min-w-[180px]">
                Classroom
              </th>

              {scheduleTimes.map((time) => (
                <th
                  key={time}
                  className="sticky top-0 z-20 bg-gray-700 border border-gray-600 p-2 text-center min-w-[72px]"
                >
                  {time}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rooms.map((room) => (
              <tr key={room} className="text-white">
                <td className="sticky left-0 z-10 bg-gray-800 border border-gray-600 p-3 text-left min-w-[180px]">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    {room}
                    {studentAccessibleRooms[room] && (
                      <UserRoundCheck
                        className="w-4 h-4 text-green-400 shrink-0"
                        title="Reservable by students"
                      />
                    )}
                  </div>
                </td>

                {scheduleTimes.map((time) => {
                  const restrictedSchedule =
                    restrictedLookup[makeSlotKey(room, dateKey, time)] || null;

                  const unavailableSchedule =
                    unavailableLookup[makeSlotKey(room, dateKey, time)] ||
                    unavailableLookup[makeSlotKey("ALL", dateKey, time)] ||
                    null;

                  const reservation = reservations[room]?.[dateKey]?.[time];
                  const isReserved = !!reservation;
                  const isRemovedByAdmin = reservation?.status === "Removed";
                  const isRestricted = !!restrictedSchedule;
                  const isUnavailable = !!unavailableSchedule;
                  const isPending = !!pendingSlots?.[room]?.[dateKey]?.[time];
                  const pendingInfo = pendingSlots?.[room]?.[dateKey]?.[time];

                  return (
                    <td
                      key={time}
                      className="border border-gray-600 p-2 text-center min-w-[72px] bg-gray-800"
                    >
                      <div className="flex justify-center items-center w-full">
                        {isRestricted ? (
                          <div
                            className="w-8 h-8 flex justify-center items-center bg-yellow-500 text-white rounded"
                            title={restrictedSchedule.ModuleName}
                          >
                            ✖
                          </div>
                        ) : isUnavailable ? (
                          <div
                            className="w-8 h-8 flex justify-center items-center bg-gray-700 text-white rounded"
                            title="Unavailable Time"
                          >
                            ✖
                          </div>
                        ) : isPending ? (
                          <div
                            className="w-8 h-8 flex justify-center items-center bg-blue-600 text-white rounded"
                            title={
                              pendingInfo
                                ? `Pending approval\n${pendingInfo.requesterName} (${pendingInfo.requesterEmail})`
                                : "Pending approval"
                            }
                          >
                            ✖
                          </div>
                        ) : isRemovedByAdmin ? (
                          <button
                            className="w-8 h-8 flex justify-center items-center bg-purple-500 text-white rounded"
                            title={`This reservation was removed by ${reservation.admin}`}
                            onClick={() => handleReserve(room, time)}
                          >
                            ✔
                          </button>
                        ) : (
                          <button
                            className={`w-8 h-8 flex justify-center items-center rounded ${
                              isReserved ? "bg-red-500" : "bg-green-500"
                            } text-white`}
                            onClick={() =>
                              isReserved
                                ? handleCancel(room, time)
                                : handleReserve(room, time)
                            }
                          >
                            {isReserved ? "✖" : "✔"}
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 text-white rounded-lg p-6 shadow-lg w-[350px]">
            <h3 className="text-lg font-semibold mb-4 text-center">
              Select a Date
            </h3>

            <div className="flex flex-col items-center">
              <DatePicker
                selected={currentDate}
                onChange={(date) => {
                  setCurrentDate(date);
                  setCurrentDay(date.getDay());
                  setIsModalOpen(false);
                }}
                inline
              />

              <button
                onClick={() => setIsModalOpen(false)}
                className="mt-4 w-32 bg-red-500 hover:bg-red-600 text-white py-2 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}
